import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AuthCard } from '@/components/auth/auth-card';
import { AuthField } from '@/components/auth/auth-field';
import { AuthForm } from '@/components/auth/auth-form';
import { Button } from '@/components/ui/button';
import { seoHead } from '@/lib/seo';
import {
    acceptStaffInviteFn,
    getStaffInviteFn,
} from '@/server/functions/auth';

const searchSchema = z.object({
    userId: z.string().optional(),
    secret: z.string().optional(),
    membershipId: z.string().optional(),
    teamId: z.string().optional(),
});

export const Route = createFileRoute('/_auth/invite')({
    component: InvitePage,
    validateSearch: searchSchema,
    head: () =>
        seoHead({
            title: 'הזמנה לצוות',
            description: 'אישור הזמנה למערכת הניהול.',
            path: '/invite',
            noIndex: true,
        }),
});

const passwordSchema = z
    .object({
        password: z.string().min(8, 'הסיסמה חייבת להכיל לפחות 8 תווים'),
        confirm: z.string().min(8, 'נא לאמת את הסיסמה'),
    })
    .refine((data) => data.password === data.confirm, {
        message: 'אימות הסיסמה אינו תואם',
        path: ['confirm'],
    });

function InvitePage() {
    const search = Route.useSearch();
    const userId = search.userId?.trim() || '';
    const secret = search.secret?.trim() || '';
    const membershipId = search.membershipId?.trim() || '';
    const teamId = search.teamId?.trim() || '';
    const valid = Boolean(userId && secret && membershipId);

    const inviteQuery = useQuery({
        queryKey: ['staff-invite', userId, membershipId, teamId],
        enabled: valid,
        queryFn: () =>
            getStaffInviteFn({
                data: {
                    userId,
                    secret,
                    membershipId,
                    teamId: teamId || undefined,
                },
            }),
    });

    const form = useForm({
        resolver: zodResolver(passwordSchema),
        defaultValues: {
            password: '',
            confirm: '',
        },
    });

    const acceptMutation = useMutation({
        mutationFn: (password?: string) =>
            acceptStaffInviteFn({
                data: {
                    userId,
                    secret,
                    membershipId,
                    teamId: teamId || undefined,
                    password,
                },
            }),
        onError: (error: Error) => {
            form.setError('root', { message: error.message });
        },
    });

    if (!valid) {
        return (
            <AuthCard
                title="הקישור אינו תקף"
                description="ייתכן שההזמנה פגה או שכבר נעשה בה שימוש."
                footer={
                    <Link to="/sign-in" className="!no-underline hover:underline">
                        התחברות
                    </Link>
                }
            >
                <p className="text-sm text-white/55">
                    חסרים פרטים בכתובת. בקשו הזמנה חדשה ממנהל המערכת.
                </p>
            </AuthCard>
        );
    }

    if (inviteQuery.isPending) {
        return (
            <AuthCard title="הזמנה לצוות" description="טוענים את ההזמנה…">
                <p className="text-sm text-white/55">רגע אחד.</p>
            </AuthCard>
        );
    }

    if (inviteQuery.isError || !inviteQuery.data) {
        return (
            <AuthCard
                title="ההזמנה אינה תקינה"
                description="לא הצלחנו לאמת את הקישור."
                footer={
                    <Link to="/sign-in" className="!no-underline hover:underline">
                        התחברות
                    </Link>
                }
            >
                <p className="text-sm text-white/55">
                    {inviteQuery.error instanceof Error
                        ? inviteQuery.error.message
                        : 'בקשו הזמנה חדשה ממנהל המערכת.'}
                </p>
            </AuthCard>
        );
    }

    const invite = inviteQuery.data;

    if (invite.confirm) {
        return (
            <AuthCard
                title="כבר הצטרפתם"
                description="ההזמנה כבר אושרה. אפשר להתחבר."
                footer={
                    <Link to="/sign-in" className="!no-underline hover:underline">
                        התחברות
                    </Link>
                }
            >
                <Button asChild className="w-full rounded-none">
                    <Link to="/sign-in">מעבר להתחברות</Link>
                </Button>
            </AuthCard>
        );
    }

    return (
        <AuthCard
            title="הזמנה לצוות"
            description={
                invite.needsPassword
                    ? `בחרו סיסמה לחשבון ${invite.email} כדי להשלים את ההצטרפות.`
                    : `אשרו את ההזמנה לחשבון ${invite.email}.`
            }
            footer={
                <Link to="/sign-in" className="!no-underline hover:underline">
                    כבר יש לכם גישה? התחברות
                </Link>
            }
        >
            {invite.needsPassword ? (
                <AuthForm
                    schema={passwordSchema}
                    defaultValues={{ password: '', confirm: '' }}
                    onSubmit={(data) => acceptMutation.mutate(data.password)}
                    submitText="אישור הזמנה"
                    isLoading={acceptMutation.isPending}
                    form={form}
                >
                    {(f) => (
                        <>
                            <AuthField
                                control={f.control}
                                name="password"
                                label="סיסמה"
                                placeholder="לפחות 8 תווים"
                                type="password"
                            />
                            <AuthField
                                control={f.control}
                                name="confirm"
                                label="אימות סיסמה"
                                placeholder="הזינו שוב את הסיסמה"
                                type="password"
                            />
                        </>
                    )}
                </AuthForm>
            ) : (
                <div className="space-y-4">
                    {form.formState.errors.root ? (
                        <div className="text-sm font-medium text-destructive">
                            {form.formState.errors.root.message}
                        </div>
                    ) : null}
                    <Button
                        type="button"
                        className="w-full rounded-none"
                        disabled={acceptMutation.isPending}
                        onClick={() => acceptMutation.mutate(undefined)}
                    >
                        אישור הזמנה
                    </Button>
                </div>
            )}
        </AuthCard>
    );
}
